import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import WriteReviewModal from "../../components/WriteReviewModal";
import FlagModal from "../../components/FlagModal";
import LikeButton from "../../components/LikeButton";
import FlagButton from "../../components/FlagButton";
import BanProposalModal from "../../components/BanProposalModal";
import useLike from "../../hooks/useLike";
import useFlag from "../../hooks/useFlag";
import StarRating from "../../components/StarRating";
import EventAttendees from "../../components/EventAttendees";
import GiveKudos from "../../components/GiveKudos";
import EventMap from "../../components/EventMap/EventMap";
import ReviewUserImage from "../../img/icons/ReviewUserImage.png";
import ShareReviewsImage from "../../img/icons/1_share_reviews.png";
import BanIcon from "../../img/icons/ban1.png";
import './EventDetailPage.css';

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [event, setEvent] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [reviewFilter, setReviewFilter] = useState('date'); // 'date', 'rating', 'likes'
  const [filterOrder, setFilterOrder] = useState('desc'); // 'desc' or 'asc'
  const [kudosRefreshKey, setKudosRefreshKey] = useState(0); // Triggers GiveKudos refresh
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [aboutOverflows, setAboutOverflows] = useState(false);
  const [artistsExpanded, setArtistsExpanded] = useState(false);
  const aboutContentRef = useRef(null);
  const aboutCardRef = useRef(null);

  // Ban proposal state
  const [showBanModal, setShowBanModal] = useState(false);
  const [isBanSubmitting, setIsBanSubmitting] = useState(false);
  const [hasProposedBan, setHasProposedBan] = useState(false);

  // Handler for when attendance changes - refreshes GiveKudos
  const handleAttendanceChange = useCallback(() => {
    setKudosRefreshKey(prev => prev + 1);
  }, []);

  // Handle filter button click - toggle between desc/asc or switch filter
  const handleFilterClick = (filter) => {
    if (reviewFilter === filter) {
      // Same filter clicked - toggle order
      setFilterOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      // Different filter - switch to it with desc order
      setReviewFilter(filter);
      setFilterOrder('desc');
    }
  };

  // Like hook for reviews
  const {
    toggleLike: toggleReviewLike,
    isLiked: isReviewLiked,
    getLikeCount: getReviewLikeCount,
    initializeBatch: initializeReviewLikes
  } = useLike({
    entityType: 'review',
    isLoggedIn: () => !!user,
    onAuthRequired: () => navigate('/login'),
    onError: (msg) => console.error(msg)
  });

  // Flag hook for reviews
  const {
    isModalOpen: isReviewFlagModalOpen,
    isSubmitting: isReviewFlagSubmitting,
    alreadyFlagged: reviewAlreadyFlagged,
    flagReasons: reviewFlagReasons,
    openFlagModal: openReviewFlagModal,
    closeFlagModal: closeReviewFlagModal,
    submitFlag: submitReviewFlag,
    isFlagged: isReviewFlagged,
  } = useFlag({
    contentType: 'review',
    isLoggedIn: () => !!user,
    onAuthRequired: () => navigate('/login'),
    onError: (msg) => alert(msg),
    onSuccess: (contentId, wasAutoHidden) => {
      if (wasAutoHidden) {
        alert('Thank you for your report. This content has been automatically hidden pending admin review.');
      } else {
        alert('Thank you for your report. Our team will review this content shortly.');
      }
      loadEvent();
    }
  });

  // Flag hook for events/festivals
  const {
    isModalOpen: isEventFlagModalOpen,
    isSubmitting: isEventFlagSubmitting,
    alreadyFlagged: eventAlreadyFlagged,
    flagReasons: eventFlagReasons,
    openFlagModal: openEventFlagModal,
    closeFlagModal: closeEventFlagModal,
    submitFlag: submitEventFlag,
    isFlagged: isEventFlagged,
  } = useFlag({
    contentType: 'event',
    isLoggedIn: () => !!user,
    onAuthRequired: () => navigate('/login'),
    onError: (msg) => alert(msg),
    onSuccess: (contentId, wasAutoHidden) => {
      if (wasAutoHidden) {
        alert('Thank you for your report. This event listing has been flagged for admin review.');
      } else {
        alert('Thank you for your report. Our team will review this event listing shortly.');
      }
    }
  });

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      loadEvent();
    }
  }, [id]);

  const getToken = () => localStorage.getItem('token');

  const loadEvent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/events/single-events/${id}`);
      const data = await response.json();

      if (data.success && data.data) {
        const eventData = data.data.event;
        setEvent(eventData);
        setFeedbacks(data.data.feedbacks || []);
        loadRatingSummary();
        // Check if current user has liked this event
        if (user?._id && eventData?.likedBy) {
          setIsFavorite(eventData.likedBy.includes(user._id));
        }
      } else {
        console.error('Event not found');
      }
    } catch (error) {
      console.error('Error loading event:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRatingSummary = async () => {
    try {
      const response = await fetch(`${API_BASE}/events/rating-summary/${id}`);
      const data = await response.json();
      if (data.success) {
        setRatingSummary(data.data);
      }
    } catch (error) {
      console.error('Error loading rating summary:', error);
    }
  };

  // Currency symbol helper
  const getCurrencySymbol = (code) => {
    const symbols = {
      'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF',
      'PLN': 'zł', 'CZK': 'Kč', 'SEK': 'kr', 'NOK': 'kr',
      'DKK': 'kr', 'CAD': 'C$', 'AUD': 'A$', 'BRL': 'R$', 'MXN': 'MX$'
    };
    return symbols[code] || code || '€';
  };

  // Format price with currency
  const formatPrice = (price, currency) => {
    if (!price) return 'Price TBA';
    const symbol = getCurrencySymbol(currency);
    // If price already contains currency symbol or text, return as is
    if (/[€$£]/.test(price) || /free/i.test(price)) return price;
    return `${symbol}${price}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.getDate(),
      year: date.getFullYear(),
      weekday: date.toLocaleDateString('en-US', { weekday: 'long' }),
      full: date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const shareEvent = () => {
    if (navigator.share) {
      navigator.share({
        title: event?.title || 'Dance Festival',
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Link copied to clipboard!');
      });
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setIsFavorite(!isFavorite);

    try {
      await fetch(`${API_BASE}/events/${id}/favourite`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ favourite: !isFavorite })
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setIsFavorite(isFavorite);
    }
  };

  const handleWriteReview = () => {
    // Check if event is in the future
    if (event && new Date(event.endDateTime) > new Date()) {
      alert('Reviews can only be written for past events. This event hasn\'t ended yet.');
      return;
    }

    // Check if event is older than 12 months
    if (event) {
      const eventEndDate = new Date(event.endDateTime);
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

      if (eventEndDate < twelveMonthsAgo) {
        alert('Reviews can only be submitted within 12 months of the event ending.');
        return;
      }
    }

    // Check if user is logged in
    if (!user) {
      navigate(`/login?return=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    setShowReviewModal(true);
  };

  const handleReviewSuccess = () => {
    // Reload event to show new review
    loadEvent();
    // Refresh GiveKudos since reviewer is auto-added as attendee
    handleAttendanceChange();
  };

  // Handle ban proposal submission
  const handleBanProposal = async (reason, details) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setIsBanSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/moderation/ban-proposal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          eventId: id,
          reason,
          details
        })
      });

      const data = await response.json();
      if (data.success) {
        setHasProposedBan(true);
        alert('Your ban proposal has been submitted. Our moderation team will review it.');
        setShowBanModal(false);
      } else {
        alert(data.error || 'Failed to submit ban proposal');
      }
    } catch (error) {
      console.error('Error submitting ban proposal:', error);
      alert('Failed to submit ban proposal. Please try again.');
    } finally {
      setIsBanSubmitting(false);
    }
  };

  // Open ban proposal modal
  const handleOpenBanModal = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowBanModal(true);
  };

  // Check if the About section content overflows the collapsed height
  useEffect(() => {
    if (aboutContentRef.current) {
      const COLLAPSED_HEIGHT = 150; // px — must match CSS max-height
      setAboutOverflows(aboutContentRef.current.scrollHeight > COLLAPSED_HEIGHT);
    }
  }, [event?.description]);

  // Initialize review likes when feedbacks load
  useEffect(() => {
    if (feedbacks.length > 0) {
      initializeReviewLikes(
        feedbacks,
        (fb) => fb.likes?.includes(user?._id) || false,
        (fb) => fb.likeCount || 0
      );
    }
  }, [feedbacks, user, initializeReviewLikes]);

  // Toggle expanded state for a review
  const toggleExpandReview = useCallback((reviewId) => {
    setExpandedReviews(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  }, []);

  // Get truncated text (first 25 words)
  const getTruncatedText = useCallback((text, wordLimit = 25) => {
    if (!text) return { text: '', isTruncated: false };
    const words = text.split(/\s+/);
    if (words.length <= wordLimit) {
      return { text, isTruncated: false };
    }
    return {
      text: words.slice(0, wordLimit).join(' '),
      isTruncated: true
    };
  }, []);

  // Check if current user has already reviewed this event
  const userReview = useMemo(() => {
    if (!user || !feedbacks.length) return null;
    return feedbacks.find(fb =>
      fb.userId === user._id ||
      fb.userId?._id === user._id ||
      fb.user === user._id ||
      fb.user?._id === user._id
    );
  }, [feedbacks, user]);

  const hasUserReviewed = !!userReview;

  // Sort feedbacks based on filter and order, always keeping user's review first
  const sortedFeedbacks = useMemo(() => {
    if (!feedbacks.length) return feedbacks;

    const multiplier = filterOrder === 'desc' ? 1 : -1;

    return [...feedbacks].sort((a, b) => {
      // Always show user's review first
      if (user) {
        const aIsUser = a.userId === user._id || a.userId?._id === user._id || a.user === user._id || a.user?._id === user._id;
        const bIsUser = b.userId === user._id || b.userId?._id === user._id || b.user === user._id || b.user?._id === user._id;

        if (aIsUser && !bIsUser) return -1;
        if (!aIsUser && bIsUser) return 1;
      }

      // Then apply the selected filter with order
      switch (reviewFilter) {
        case 'rating':
          // desc = highest first, asc = lowest first
          return ((b.stars || 0) - (a.stars || 0)) * multiplier;
        case 'likes':
          // desc = most liked first, asc = least liked first
          return ((b.likeCount || 0) - (a.likeCount || 0)) * multiplier;
        case 'date':
        default:
          // desc = most recent first, asc = oldest first
          return (new Date(b.createdAt || 0) - new Date(a.createdAt || 0)) * multiplier;
      }
    });
  }, [feedbacks, user, reviewFilter, filterOrder]);

  // Render stars with half-star support using the reusable component
  const renderStars = (rating, size = 'medium') => {
    return (
      <StarRating
        rating={rating || 0}
        readOnly={true}
        size={size}
      />
    );
  };

  if (loading) {
    return (
      <div className="event-detail-page">
        <div className="loading-state">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-detail-page">
        <div className="error-state">
          <i className="bi bi-exclamation-circle"></i>
          <h3>Event not found</h3>
          <Link to={`/events${isFutureEvent ? '' : '?tab=past'}`} className="back-link">Back to Events</Link>
        </div>
      </div>
    );
  }

  const startDate = formatDate(event.startDateTime);
  const endDate = formatDate(event.endDateTime);
  const location = [event.city, event.country].filter(Boolean).join(', ') || event.location;
  const avgRating = ratingSummary?.avgOverall?.toFixed(1) || '0.0';
  const totalReviews = ratingSummary?.totalReviews || feedbacks.length || 0;
  const isFutureEvent = new Date(event.endDateTime) > new Date();

  // Check if event is within 12-month review period
  const eventEndDate = new Date(event.endDateTime);
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
  const isReviewPeriodExpired = !isFutureEvent && eventEndDate < twelveMonthsAgo;

  return (
    <div className="event-detail-page">
      {/* Hero Section */}
      <section
        className="event-hero"
        style={{
          backgroundImage: event.eventImage ? `url('${event.eventImage}')` : 'none',
          backgroundColor: event.eventImage ? 'transparent' : '#D8C8E8'
        }}
      >
        <Link to={`/events${isFutureEvent ? '' : '?tab=past'}`} className="back-btn">
          <i className="bi bi-arrow-left"></i>
        </Link>

        <div className="event-actions">
          <button className="action-btn" onClick={shareEvent} title="Share">
            <i className="bi bi-share"></i>
          </button>
          <button
            className={`action-btn ${isFavorite ? 'liked' : ''}`}
            onClick={toggleFavorite}
            title="Add to favorites"
          >
            <i className={`bi ${isFavorite ? 'bi-heart-fill' : 'bi-heart'}`}></i>
          </button>
          <button
            className={`action-btn ${isEventFlagged(id) ? 'flagged' : ''}`}
            onClick={() => openEventFlagModal(id)}
            title="Report this event"
          >
            <i className={`bi ${isEventFlagged(id) ? 'bi-flag-fill' : 'bi-flag'}`}></i>
          </button>
        </div>

        {/* Boycott button - bottom right, smaller */}
        <button
          className={`action-btn action-btn-ban ${hasProposedBan ? 'proposed' : ''}`}
          onClick={handleOpenBanModal}
          title={hasProposedBan ? 'Boycott report submitted' : 'I boycott this event'}
        >
          <img src={BanIcon} alt="Boycott" className="ban-icon" />
        </button>

        <div className="event-hero-overlay">
          <div className="hero-title-row">
            <h1 className="event-hero-title">{event.title}</h1>
            <div className="hero-date-badge">
              <div className="date-box start">
                <div className="month">{startDate.month}</div>
                <div className="day">{startDate.day}</div>
                <div className="year">{startDate.year}</div>
              </div>
              <span className="date-separator">—</span>
              <div className="date-box end">
                <div className="month">{endDate.month}</div>
                <div className="day">{endDate.day}</div>
                <div className="year">{endDate.year}</div>
              </div>
            </div>
          </div>
          <div className="event-hero-location">
            <i className="bi bi-geo-alt"></i>
            <span>{location}</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="event-content">
        {/* Left Column */}
        <div className="event-main">
          {/* Dance Styles - desktop only */}
          {event.category && event.category.length > 0 && (
            <div className="info-card desktop-only">
              <h3 className="info-card-title">
                <i className="bi bi-music-note-beamed"></i> Dance Styles
              </h3>
              <div className="dance-styles">
                {event.category.map((style, idx) => (
                  <span key={idx} className="style-tag">{style}</span>
                ))}
              </div>
            </div>
          )}

          {/* About - desktop only */}
          <div className="info-card desktop-only" ref={aboutCardRef}>
            <h3 className="info-card-title">
              <i className="bi bi-info-circle"></i> About This Event
            </h3>
            <div className={`about-collapse-wrapper ${aboutExpanded ? 'expanded' : ''}`}>
              <div className="event-description" ref={aboutContentRef}>
                {event.description || 'No description available.'}
              </div>
              {!aboutExpanded && aboutOverflows && (
                <div className="about-fade-overlay" />
              )}
            </div>
            {aboutOverflows && (
              <button
                className="about-toggle-btn"
                onClick={() => {
                  setAboutExpanded(prev => {
                    if (prev && aboutCardRef.current) {
                      setTimeout(() => {
                        aboutCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 50);
                    }
                    return !prev;
                  });
                }}
              >
                {aboutExpanded ? (
                  <><i className="bi bi-chevron-up"></i> Show less</>
                ) : (
                  <><i className="bi bi-chevron-down"></i> More details</>
                )}
              </button>
            )}
          </div>

          {/* Artists - desktop only */}
          {event.artists && event.artists.length > 0 && (() => {
            const sortedArtists = [...event.artists].sort((a, b) => {
              const aIsDJ = /^DJ\s|^DJ$/i.test(a.trim());
              const bIsDJ = /^DJ\s|^DJ$/i.test(b.trim());
              if (aIsDJ && !bIsDJ) return -1;
              if (!aIsDJ && bIsDJ) return 1;
              return 0;
            });

            return (
              <div className="info-card desktop-only">
                <h3 className="info-card-title">
                  <i className="bi bi-star"></i> Featured Artists & Instructors
                </h3>
                <div className="artists-grid">
                  {sortedArtists.map((artist, idx) => (
                    <div
                      key={idx}
                      className="artist-item artist-clickable"
                      onClick={() => navigate(`/events?artist=${encodeURIComponent(artist)}`)}
                      title={`View all events with ${artist}`}
                    >
                      <div className="artist-avatar">
                        {artist.charAt(0).toUpperCase()}
                      </div>
                      <span className="artist-name">{artist}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Reviews */}
          <div className="info-card reviews-card">
            <h3 className="info-card-title">
              <i className="bi bi-chat-quote"></i> Reviews
            </h3>

            {/* Show ratings layout only when there are reviews */}
            {totalReviews > 0 ? (
              <div className="reviews-layout">
                {/* Left - Image */}
                <div className="reviews-image-section">
                  <img src={ShareReviewsImage} alt="Share Reviews" className="share-reviews-image" />
                </div>

                {/* Center - Score + Button */}
                <div className="reviews-score-section">
                  <div className="rating-big">{avgRating}</div>
                  {renderStars(parseFloat(avgRating))}
                  <div className="rating-count">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</div>

                  {isFutureEvent ? (
                    <div className="review-note">
                      <i className="bi bi-clock"></i> Reviews available after event ends
                    </div>
                  ) : isReviewPeriodExpired ? (
                    <div className="review-note">
                      <i className="bi bi-calendar-x"></i> Review period ended (12 months)
                    </div>
                  ) : hasUserReviewed ? (
                    <div className="btn-write-review-wrapper">
                      <button className="btn-write-review disabled" disabled>
                        <i className="bi bi-check-circle"></i> Reviewed
                      </button>
                      <span className="review-tooltip">You have already reviewed this event</span>
                    </div>
                  ) : (
                    <button className="btn-write-review" onClick={handleWriteReview}>
                      <i className="bi bi-pencil"></i> Write a Review
                    </button>
                  )}
                </div>

                {/* Right - Category Ratings */}
                {ratingSummary && ratingSummary.totalReviews > 0 && (
                  <div className="category-ratings-box">
                    {ratingSummary.avgVenue > 0 && (
                      <div className="category-rating-item">
                        <span className="cat-label" data-tooltip="Quality of the dance floor, air circulation, acoustics, lighting, and overall atmosphere. Was it spacious and comfortable?">Venue</span>
                        <span className="cat-value">{ratingSummary.avgVenue.toFixed(1)}</span>
                      </div>
                    )}
                    {ratingSummary.avgRatio > 0 && (
                      <div className="category-rating-item">
                        <span className="cat-label" data-tooltip="Balance between leads and follows. Were there taxi dancers? Were you able to find dance partners easily throughout the event?">Ratio</span>
                        <span className="cat-value">{ratingSummary.avgRatio.toFixed(1)}</span>
                      </div>
                    )}
                    {ratingSummary.avgOrganization > 0 && (
                      <div className="category-rating-item">
                        <span className="cat-label" data-tooltip="Event logistics, schedule, communication, registration process, and staff helpfulness.">Organization</span>
                        <span className="cat-value">{ratingSummary.avgOrganization.toFixed(1)}</span>
                      </div>
                    )}
                    {ratingSummary.avgArtists > 0 && (
                      <div className="category-rating-item">
                        <span className="cat-label" data-tooltip="Were DJs and Instructors able to create a rich & consistently enjoyable experience for the attendees?">Artists</span>
                        <span className="cat-value">{ratingSummary.avgArtists.toFixed(1)}</span>
                      </div>
                    )}
                    {ratingSummary.avgCulture > 0 && (
                      <div className="category-rating-item">
                        <span className="cat-label" data-tooltip="Did it feel like a friendly, inclusive, and respectful community? Were those values encouraged? Were there discussions on dance roots and etiquette?">Culture</span>
                        <span className="cat-value">{ratingSummary.avgCulture.toFixed(1)}</span>
                      </div>
                    )}
                    {ratingSummary.avgSafety > 0 && (
                      <div className="category-rating-item">
                        <span className="cat-label" data-tooltip="Did it feel like a secure and safe environment? Were there materials or workshops on consent awareness; appointed harassment prevention team members?">Safety</span>
                        <span className="cat-value">{ratingSummary.avgSafety.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* No reviews yet - show prompt to write first review */
              <div className="no-reviews-prompt">
                <div className="no-reviews-image">
                  <img src={ShareReviewsImage} alt="Share Reviews" className="share-reviews-image" />
                </div>
                <div className="no-reviews-content">
                  <p className="no-reviews-text">No reviews yet. Be the first to share your experience!</p>
                  {isFutureEvent ? (
                    <div className="review-note">
                      <i className="bi bi-clock"></i> Reviews available after event ends
                    </div>
                  ) : isReviewPeriodExpired ? (
                    <div className="review-note">
                      <i className="bi bi-calendar-x"></i> Review period ended (12 months)
                    </div>
                  ) : (
                    <button className="btn-write-review" onClick={handleWriteReview}>
                      <i className="bi bi-pencil"></i> Write the First Review
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Highlight Tags */}
            {ratingSummary?.highlightTags?.length > 0 && (
              <div className="highlight-tags">
                {ratingSummary.highlightTags.slice(0, 4).map((tag, idx) => (
                  <span
                    key={idx}
                    className={`highlight-tag highlight-tag--${tag.key}`}
                    title={tag.tooltip}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            )}

            {/* Review Filters */}
            {feedbacks.length > 0 && (
              <div className="review-filters">
                <span className="filter-label">Sort by:</span>
                <button
                  className={`filter-btn ${reviewFilter === 'date' ? (filterOrder === 'desc' ? 'active-desc' : 'active-asc') : ''}`}
                  onClick={() => handleFilterClick('date')}
                >
                  <i className="bi bi-calendar3"></i> Date
                </button>
                <button
                  className={`filter-btn ${reviewFilter === 'rating' ? (filterOrder === 'desc' ? 'active-desc' : 'active-asc') : ''}`}
                  onClick={() => handleFilterClick('rating')}
                >
                  <i className="bi bi-star-fill"></i> Rating
                </button>
                <button
                  className={`filter-btn ${reviewFilter === 'likes' ? (filterOrder === 'desc' ? 'active-desc' : 'active-asc') : ''}`}
                  onClick={() => handleFilterClick('likes')}
                >
                  <i className="bi bi-heart-fill"></i> Most Liked
                </button>
              </div>
            )}

            {/* Reviews List */}
            {sortedFeedbacks.length > 0 ? (
              <div className="reviews-list">
                {sortedFeedbacks.map((fb, idx) => {
                  const roleLabels = {
                    attendee: { label: 'Attendee', icon: 'bi-person' },
                    volunteer: { label: 'Volunteer', icon: 'bi-hand-thumbs-up' },
                    artist: { label: 'Artist', icon: 'bi-star' },
                    invited_guest: { label: 'Invited Guest', icon: 'bi-gift' }
                  };
                  const roleInfo = roleLabels[fb.role] || roleLabels.attendee;
                  const isOwnReview = user && (
                    fb.userId === user._id ||
                    fb.userId?._id === user._id ||
                    fb.user === user._id ||
                    fb.user?._id === user._id
                  );
                  const reviewText = fb.description || fb.comment || '';
                  const { text: truncatedText, isTruncated } = getTruncatedText(reviewText, 25);
                  const isExpanded = expandedReviews[fb._id];
                  const reviewId = fb._id;

                  return (
                    <div key={idx} className={`review-card ${isOwnReview ? 'own-review' : ''}`}>
                      {isOwnReview && (
                        <div className="own-review-badge">
                          <i className="bi bi-person-check"></i> Your Review
                        </div>
                      )}

                      <div className="review-layout">
                        {/* Left: Avatar Section */}
                        <div className="review-avatar-section">
                          <div className="review-user-image-container">
                            <img src={ReviewUserImage} alt="Reviewer" className="review-user-image" />
                          </div>
                          {!fb.hideRole && (
                            <div className="review-role-label">{roleInfo.label}</div>
                          )}
                          {fb.hideRole && (
                            <div className="review-role-label anonymous">
                              <i className="bi bi-incognito"></i> Anonymous
                            </div>
                          )}
                          <div className="review-stars-small">
                            {renderStars(fb.stars, 'small')}
                          </div>
                        </div>

                        {/* Center: Content Bubble */}
                        <div className="review-bubble-wrapper">
                          <div className="review-bubble">
                            {/* Category Ratings */}
                            <div className="review-category-ratings">
                              {fb.venue > 0 && <span><i className="bi bi-building"></i> Venue: {fb.venue}</span>}
                              {fb.ratio > 0 && <span><i className="bi bi-yin-yang"></i> Ratio: {fb.ratio}</span>}
                              {fb.organization > 0 && <span><i className="bi bi-clipboard-check"></i> Org: {fb.organization}</span>}
                              {fb.artists > 0 && <span><i className="bi bi-music-note"></i> Artists: {fb.artists}</span>}
                              {fb.culture > 0 && <span><i className="bi bi-globe"></i> Culture: {fb.culture}</span>}
                              {fb.safety > 0 && <span><i className="bi bi-shield-check"></i> Safety: {fb.safety}</span>}
                            </div>

                            {/* Review Title */}
                            {fb.title && <div className="review-title">{fb.title}</div>}

                            {/* Review Content */}
                            <div className="review-content">
                              <p className="review-text">
                                {isExpanded ? reviewText : truncatedText}
                                {isTruncated && !isExpanded && '...'}
                              </p>
                              {isTruncated && (
                                <button
                                  className="expand-review-btn"
                                  onClick={() => toggleExpandReview(reviewId)}
                                >
                                  <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-three-dots'}`}></i>
                                  {isExpanded ? ' Show less' : ''}
                                </button>
                              )}
                            </div>

                          </div>

                          {/* Actions: Like & Flag - Outside the bubble */}
                          <div className="review-external-actions">
                            <LikeButton
                              isLiked={isReviewLiked(reviewId)}
                              likeCount={getReviewLikeCount(reviewId)}
                              onToggle={() => toggleReviewLike(reviewId)}
                              size="small"
                              variant="minimal"
                            />
                            <FlagButton
                              onClick={() => openReviewFlagModal(reviewId)}
                              isFlagged={isReviewFlagged(reviewId)}
                              size="small"
                              title="Report this review"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        {/* Right Column (Sidebar) */}
        <div className="event-sidebar">
          {/* Location */}
          <div className="info-card">
            <h3 className="info-card-title">
              <i className="bi bi-geo-alt"></i> Location
            </h3>
            <EventMap
              lat={event.coordinates?.lat}
              lng={event.coordinates?.lng}
              title={event.title}
            />
            <div className="location-details">
              {event.location && (
                <p><i className="bi bi-building"></i> {event.location}</p>
              )}
              {event.venueAddress && (
                <p><i className="bi bi-signpost"></i> {event.venueAddress}</p>
              )}
              {location && (
                <p><i className="bi bi-geo"></i> {location}</p>
              )}
            </div>
          </div>

          {/* Pricing & Organizer */}
          <div className="info-card">
            <h3 className="info-card-title">
              <i className="bi bi-ticket-perforated"></i> Tickets & Org
            </h3>
            <div className="price-display">{formatPrice(event.price, event.currency)}</div>

            {/* Get my pass button */}
            {event.ticketLink && (
              isFutureEvent ? (
                <a href={event.ticketLink} className="btn-get-pass" target="_blank" rel="noopener noreferrer">
                  <i className="bi bi-ticket-detailed"></i> Get My Pass
                </a>
              ) : (
                <span className="btn-get-pass btn-get-pass--disabled">
                  <i className="bi bi-ticket-detailed"></i> Get My Pass
                </span>
              )
            )}

            {/* Display multiple organizers if available, fall back to single organizer */}
            {event.organizers && event.organizers.length > 0 ? (
              <div className="organizers-list">
                <div className="org-section-label">Organized by</div>
                {event.organizers.map((org, idx) => (
                  <div
                    key={idx}
                    className="organizer-info organizer-clickable"
                    onClick={() => navigate(`/events?organizer=${encodeURIComponent(org)}`)}
                    title={`View all events by ${org}`}
                  >
                    <div className="organizer-avatar organizer-avatar-small">
                      <i className="bi bi-person"></i>
                    </div>
                    <div className="organizer-details">
                      <div className="name organizer-link">{org}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : event.organizer && (
              <div
                className="organizer-info organizer-clickable"
                onClick={() => navigate(`/events?organizer=${encodeURIComponent(event.organizer)}`)}
                title={`View all events by ${event.organizer}`}
              >
                <div className="organizer-avatar organizer-avatar-small">
                  <i className="bi bi-person"></i>
                </div>
                <div className="organizer-details">
                  <div className="name organizer-link">{event.organizer}</div>
                  <div className="org-label">Event Organizer</div>
                </div>
              </div>
            )}

            {/* Event Series & Edition */}
            {event.edition && (
              <div className="event-series-info">
                <div
                  className="event-series-row event-series-row--clickable"
                  onClick={() => {
                    if (event.edition > 1 && event.eventSeries) {
                      navigate(`/events?series=${encodeURIComponent(event.eventSeries)}`);
                    } else {
                      const organizer = event.organizers?.[0] || event.organizer;
                      if (organizer) {
                        navigate(`/events?organizer=${encodeURIComponent(organizer)}`);
                      }
                    }
                  }}
                  title={event.edition > 1 ? "See all editions of this event series" : "See all events by this organizer"}
                >
                  <span className="event-series-label">Event Series:</span>
                  <span className="event-series-name">{event.eventSeries || event.title}</span>
                </div>
                <div className="event-series-row">
                  <span className="event-series-label">Edition:</span>
                  <span className="event-series-value">
                    {event.edition > 1
                      ? `${event.edition}${event.edition === 2 ? 'nd' : event.edition === 3 ? 'rd' : 'th'}`
                      : 'New'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Dance Styles - mobile/tablet only */}
          {event.category && event.category.length > 0 && (
            <div className="info-card mobile-only">
              <h3 className="info-card-title">
                <i className="bi bi-music-note-beamed"></i> Dance Styles
              </h3>
              <div className="dance-styles">
                {event.category.map((style, idx) => (
                  <span key={idx} className="style-tag">{style}</span>
                ))}
              </div>
            </div>
          )}

          {/* About - mobile/tablet only */}
          <div className="info-card mobile-only">
            <h3 className="info-card-title">
              <i className="bi bi-info-circle"></i> About This Event
            </h3>
            <div className={`about-collapse-wrapper ${aboutExpanded ? 'expanded' : ''}`}>
              <div className="event-description">
                {event.description || 'No description available.'}
              </div>
              {!aboutExpanded && aboutOverflows && (
                <div className="about-fade-overlay" />
              )}
            </div>
            {aboutOverflows && (
              <button
                className="about-toggle-btn"
                onClick={() => setAboutExpanded(prev => !prev)}
              >
                {aboutExpanded ? (
                  <><i className="bi bi-chevron-up"></i> Show less</>
                ) : (
                  <><i className="bi bi-chevron-down"></i> More details</>
                )}
              </button>
            )}
          </div>

          {/* Featured Artists - mobile/tablet only (expandable, 2 per row) */}
          {event.artists && event.artists.length > 0 && (() => {
            const sortedArtists = [...event.artists].sort((a, b) => {
              const aIsDJ = /^DJ\s|^DJ$/i.test(a.trim());
              const bIsDJ = /^DJ\s|^DJ$/i.test(b.trim());
              if (aIsDJ && !bIsDJ) return -1;
              if (!aIsDJ && bIsDJ) return 1;
              return 0;
            });
            const maxVisible = 8; // 4 rows x 2 names
            const hasMore = sortedArtists.length > maxVisible;
            const visibleArtists = artistsExpanded ? sortedArtists : sortedArtists.slice(0, maxVisible);

            return (
              <div className="info-card mobile-only">
                <h3 className="info-card-title">
                  <i className="bi bi-star"></i> Featured Artists & Instructors
                </h3>
                <div className="artists-grid artists-grid--sidebar">
                  {visibleArtists.map((artist, idx) => (
                    <div
                      key={idx}
                      className="artist-item artist-clickable"
                      onClick={() => navigate(`/events?artist=${encodeURIComponent(artist)}`)}
                      title={`View all events with ${artist}`}
                    >
                      <div className="artist-avatar">
                        {artist.charAt(0).toUpperCase()}
                      </div>
                      <span className="artist-name">{artist}</span>
                    </div>
                  ))}
                </div>
                {hasMore && (
                  <button
                    className="about-toggle-btn"
                    onClick={() => setArtistsExpanded(prev => !prev)}
                  >
                    {artistsExpanded ? (
                      <><i className="bi bi-chevron-up"></i> Show less</>
                    ) : (
                      <><i className="bi bi-chevron-down"></i> +{sortedArtists.length - maxVisible} more</>
                    )}
                  </button>
                )}
              </div>
            );
          })()}

          {/* Attendees - Above CTA for past events */}
          {!isFutureEvent && (
            <>
              <EventAttendees
                eventId={id}
                isPastEvent={true}
                fbRespondents={event.fbRespondents || 0}
                onAttendanceChange={handleAttendanceChange}
              />
              <GiveKudos
                eventId={id}
                isPastEvent={true}
                refreshKey={kudosRefreshKey}
              />
            </>
          )}

          {/* CTA */}
          {event.eventlink && event.eventlink !== 'manual-entry' && (
            <div className="cta-section">
              <h3>Interested?</h3>
              <p>Get more details and book your spot!</p>
              <a href={event.eventlink} className="btn-cta" target="_blank" rel="noopener noreferrer">
                <i className="bi bi-box-arrow-up-right"></i> View on Facebook
              </a>
            </div>
          )}

          {/* Attendees - Below CTA for future events */}
          {isFutureEvent && (
            <>
              <EventAttendees
                eventId={id}
                isPastEvent={false}
                fbRespondents={event.fbRespondents || 0}
                onAttendanceChange={handleAttendanceChange}
              />
              <GiveKudos
                eventId={id}
                isPastEvent={false}
                refreshKey={kudosRefreshKey}
              />
            </>
          )}
        </div>
      </div>

      {/* Write Review Modal */}
      <WriteReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        eventId={id}
        eventTitle={event?.title}
        eventArtists={event?.artists || []}
        onSuccess={handleReviewSuccess}
        apiBase={API_BASE}
        onOpenBoycott={() => {
          setShowReviewModal(false);
          setShowBanModal(true);
        }}
      />

      {/* Flag Review Modal */}
      <FlagModal
        isOpen={isReviewFlagModalOpen}
        onClose={closeReviewFlagModal}
        onSubmit={submitReviewFlag}
        isSubmitting={isReviewFlagSubmitting}
        alreadyFlagged={reviewAlreadyFlagged}
        flagReasons={reviewFlagReasons}
        contentType="review"
      />

      {/* Flag Event Modal */}
      <FlagModal
        isOpen={isEventFlagModalOpen}
        onClose={closeEventFlagModal}
        onSubmit={submitEventFlag}
        isSubmitting={isEventFlagSubmitting}
        alreadyFlagged={eventAlreadyFlagged}
        flagReasons={eventFlagReasons}
        contentType="event"
      />

      {/* Boycott Modal */}
      <BanProposalModal
        isOpen={showBanModal}
        onClose={() => setShowBanModal(false)}
        onSubmit={handleBanProposal}
        isSubmitting={isBanSubmitting}
        alreadyProposed={hasProposedBan}
        eventTitle={event?.title}
      />
    </div>
  );
};

export default EventDetailPage;
